/**
 * Text formatting utilities for various text transformations
 */
export class TextFormatter {
  /**
   * Remove special characters (@, _, #, $) and trim whitespace
   */
  static clean(input: string): string {
    if (!input || typeof input !== "string") return "";
    return input.replace(/[@_#$]/g, "").trim();
  }

  /**
   * Format text to title case (capitalize first letter of each word)
   */
  static titleCase(input: string): string {
    if (!input || typeof input !== "string") return "";
    return input
      .toLowerCase()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ")
      .trim();
  }

  /**
   * Format name (capitalize first letter, lowercase rest)
   */
  static formatName(input: string): string {
    if (!input || typeof input !== "string") return "";
    return input.charAt(0).toUpperCase() + input.slice(1).toLowerCase();
  }

  /**
   * Create a URL-friendly slug from text
   */
  static slugify(input: string): string {
    if (!input || typeof input !== "string") return "";
    return input
      .toLowerCase()
      .replace(/[^\w\s-]/g, "") // Remove special characters except spaces and hyphens
      .replace(/[\s_-]+/g, "-") // Replace spaces, underscores, multiple hyphens with single hyphen
      .replace(/^-+|-+$/g, "") // Remove leading/trailing hyphens
      .trim();
  }

  /**
   * Truncate text to specified length with ellipsis
   */
  static truncate(
    input: string,
    maxLength: number = 100,
    suffix: string = "...",
  ): string {
    if (!input || typeof input !== "string") return "";
    if (input.length <= maxLength) return input;
    return input.substring(0, maxLength - suffix.length) + suffix;
  }

  /**
   * Format phone number (basic US format)
   */
  static formatPhone(input: string): string {
    if (!input || typeof input !== "string") return "";
    const cleaned = input.replace(/\D/g, "");
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(
        6,
      )}`;
    }
    if (cleaned.length === 11 && cleaned.startsWith("1")) {
      return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(
        4,
        7,
      )}-${cleaned.slice(7)}`;
    }
    return input; // Return original if format doesn't match
  }

  /**
   * Remove all non-numeric characters
   */
  static numericOnly(input: string): string {
    if (!input || typeof input !== "string") return "";
    return input.replace(/\D/g, "");
  }

  /**
   * Remove all non-alphabetic characters
   */
  static alphaOnly(input: string): string {
    if (!input || typeof input !== "string") return "";
    return input.replace(/[^a-zA-Z\s]/g, "").trim();
  }

  /**
   * Remove extra whitespace and normalize spaces
   */
  static normalizeSpaces(input: string): string {
    if (!input || typeof input !== "string") return "";
    return input.replace(/\s+/g, " ").trim();
  }

  /**
   * Capitalize first letter only
   */
  static capitalize(input: string): string {
    if (!input || typeof input !== "string") return "";
    return input.charAt(0).toUpperCase() + input.slice(1).toLowerCase();
  }

  /**
   * Convert to uppercase
   */
  static uppercase(input: string): string {
    if (!input || typeof input !== "string") return "";
    return input.toUpperCase();
  }

  /**
   * Convert to lowercase
   */
  static lowercase(input: string): string {
    if (!input || typeof input !== "string") return "";
    return input.toLowerCase();
  }

  /**
   * Convert snake_case or SCREAMING_SNAKE_CASE to readable text
   */
  static humanize(input: string): string {
    if (!input || typeof input !== "string") return "";
    return input
      .toLowerCase()
      .replace(/_+/g, " ") // Replace underscores with spaces
      .trim();
  }

  /**
   * Convert snake_case to Title Case
   */
  static humanizeTitle(input: string): string {
    if (!input || typeof input !== "string") return "";
    return this.titleCase(this.humanize(input));
  }

  /**
   * Auto-detect and format text based on its current format
   */
  static autoFormat(input: string): string {
    if (!input || typeof input !== "string") return "";

    // Check if it's snake_case or SCREAMING_SNAKE_CASE
    if (input.includes("_")) {
      return this.humanizeTitle(input);
    }

    // Check if it's ALL_CAPS
    if (input === input.toUpperCase() && input.length > 1) {
      return this.titleCase(input.toLowerCase());
    }

    // Check if it's camelCase
    if (/[a-z][A-Z]/.test(input)) {
      return this.titleCase(input.replace(/([a-z])([A-Z])/g, "$1 $2"));
    }

    // Check if it's kebab-case
    if (input.includes("-")) {
      return this.titleCase(input.replace(/-/g, " "));
    }

    // If already properly formatted, return as is
    return input;
  }
}

// Legacy function for backward compatibility
export function textFormatter(input: string): string {
  return TextFormatter.clean(input);
}
